import { useState } from 'react';
import type { AtlasManifest } from '@atlas-manifest/types';
import type { Sandbox } from '../state/atlasApiTypes.js';
import { applyOps, normalizeSandboxOps } from '../state/sandboxOps.js';
import { navigateSimulateSandbox } from '../state/hashRouter.js';
import './PublishView.css';

export interface PublishViewProps {
  manifest: AtlasManifest;
  sandbox: Sandbox;
  onNavigateBack: () => void;
  onPublish: (
    sandboxId: string,
  ) => Promise<
    | { ok: true; sandboxId: string; prUrl: string; prNumber: number; publishedAt: number }
    | { ok: false; error: string }
  >;
}

interface EntityRowPreview {
  entityId: string;
  path: string;
  fieldCount: number;
  refs: string[];
}

function generateEntityRows(sandbox: Sandbox, manifest: AtlasManifest): EntityRowPreview[] {
  const ops = normalizeSandboxOps(sandbox.ops)
    .filter((entry) => entry.state === 'accepted')
    .map((entry) => entry.op);
  const draft = applyOps(manifest, ops);
  const rows: EntityRowPreview[] = [];
  for (const op of ops) {
    if (op.kind !== 'create-entity') continue;
    // All ops have been validated by normalizeSandboxOps; applyOps guarantees
    // that every create-entity op results in an entity in draft.entities.
    const entity = draft.entities.find((e) => e.id === op.entityId)!;
    rows.push({
      entityId: entity.id,
      path: `/users/{uid}/${entity.domain}/${entity.id.toLowerCase()}`,
      fieldCount: entity.fields.length,
      refs: draft.references.filter((r) => r.from === entity.id).map((r) => r.to),
    });
  }
  return rows;
}

/**
 * Two-step sandbox publication wizard.
 * Step 1: Preview and commit the entity row to docs/impl
 * Step 2: Generate code scaffolds (enabled only after Step 1 completes)
 */
export function PublishView({ manifest, sandbox, onNavigateBack, onPublish }: PublishViewProps): React.ReactElement {
  const [isPublishing, setIsPublishing] = useState(false);
  const [result, setResult] = useState<
    | { ok: true; prUrl: string }
    | { ok: false; error: string }
    | null
  >(null);

  const handlePublish = async () => {
    setIsPublishing(true);
    try {
      const response = await onPublish(sandbox.id);
      setResult(response.ok ? { ok: true, prUrl: response.prUrl } : { ok: false, error: response.error });
    } finally {
      setIsPublishing(false);
    }
  };

  const publishComplete = result?.ok === true;

  return (
    <div className="publish-view">
      <div className="detail-topbar">
        <button className="btn btn-ghost" onClick={onNavigateBack}>
          ← {sandbox.name}
        </button>
        <h1 className="detail-title">Publish</h1>
      </div>

      <div className="publish-stepper">
        <div className={`publish-step ${publishComplete ? 'done' : 'current'}`}>
          <span className="publish-step-dot">{publishComplete ? '✓' : '1'}</span>
          Step 1: Commit to docs/impl
        </div>
        <div className={`publish-step-line ${publishComplete ? 'done' : ''}`}></div>
        <div className={`publish-step ${publishComplete ? 'current' : ''}`}>
          <span className="publish-step-dot">2</span>
          Step 2: Generate code scaffold
        </div>
      </div>

      <div className="section-label">Entity row(s) to be committed</div>
      <div className="card">
        <p>
          The entity row(s) below will be added to the unified catalog HTML and become parseable by the
          manifest builder.
        </p>
        <table className="publish-preview-table">
          <thead>
            <tr>
              <th>Entity</th>
              <th>Path</th>
              <th>Fields</th>
            </tr>
          </thead>
          <tbody>
            {generateEntityRows(sandbox, manifest).map((row) => (
              <tr key={row.entityId}>
                <td>{row.entityId}</td>
                <td>{row.path}</td>
                <td>{row.fieldCount}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <button className="btn btn-ghost" onClick={() => navigateSimulateSandbox(sandbox.id)}>
          Simulate this draft
        </button>

        <button className="btn btn-primary" onClick={handlePublish} disabled={isPublishing || publishComplete}>
          {isPublishing ? 'Publishing…' : publishComplete ? 'Published' : 'Publish to docs'}
        </button>
        {result?.ok === false && <p className="publish-error">{result.error}</p>}
        {result?.ok === true && <p className="publish-success"><a href={result.prUrl}>{result.prUrl}</a></p>}
      </div>

      <div className="section-label">Code scaffold generation</div>
      <div className={`card ${publishComplete ? '' : 'publish-step2-pending'}`}>
        <p>
          Once Step 1 completes, TypeScript interfaces, Firestore rules, and Cloud Function stubs will be
          generated.
        </p>
        <button className="btn btn-primary" disabled={!publishComplete} title={!publishComplete ? 'Complete Step 1 first' : undefined}>
          Generate Code
        </button>
      </div>
    </div>
  );
}
